import toAiChatContext from 'utils/toAiChatContext';

import { dossierChatScreen } from './watchChatScreen';

import type { WatchCompany } from './types';

/**
 * Serialized chatContext cap. Mirrors backend AI_CHATS_CONTEXT_MAX_LENGTH.
 * Change both together.
 * @see docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md
 */
export const AI_CHAT_CONTEXT_MAX_CHARS = 4000;

/**
 * One stored step response the server may load by reference.
 * A longer response is refused. Do not shorten the JSON.
 */
export const INVESTIGATION_STEP_RESPONSE_MAX_CHARS = 100000;

export interface IInvestigationStepAttachment {
  watchCompanyId: number,
  runId: number,
  stepIndex: number,
  promptRole: string,
  responseJson: unknown,
}

export type InvestigationStepWire = 'inline' | 'reference' | 'over_cap';

export interface IInvestigationStepChatPlan {
  wire: InvestigationStepWire,
  responseChars: number,
  customData?: Record<string, unknown>,
}

function serializedLength(value: unknown): number {
  const text = JSON.stringify(value);
  return text ? text.length : 0;
}

function contextLength(
  company: WatchCompany,
  context: Record<string, unknown>,
  extra?: Record<string, unknown>,
): number {
  return serializedLength(toAiChatContext(dossierChatScreen(company, context, extra)));
}

/**
 * Put responseJson on the dossier chat only when the full chatContext stays
 * within 4000 characters. A larger step sends a reference, not a shortened body.
 */
export function planInvestigationStepChat(
  company: WatchCompany,
  context: Record<string, unknown>,
  attachment: IInvestigationStepAttachment,
): IInvestigationStepChatPlan {
  const responseChars = serializedLength(attachment.responseJson);
  const identity = {
    watchCompanyId: attachment.watchCompanyId,
    runId: attachment.runId,
    stepIndex: attachment.stepIndex,
    promptRole: attachment.promptRole,
  };
  const inlineData = {
    investigationStep: {
      ...identity,
      responseJson: attachment.responseJson,
    },
  };
  if (contextLength(company, context, inlineData) <= AI_CHAT_CONTEXT_MAX_CHARS) {
    return { wire: 'inline', responseChars, customData: inlineData };
  }
  if (responseChars > INVESTIGATION_STEP_RESPONSE_MAX_CHARS) {
    return { wire: 'over_cap', responseChars };
  }
  const referenceData = {
    investigationStepRef: {
      ...identity,
      responseChars,
    },
  };
  if (contextLength(company, context, referenceData) > AI_CHAT_CONTEXT_MAX_CHARS) {
    return { wire: 'over_cap', responseChars };
  }
  return { wire: 'reference', responseChars, customData: referenceData };
}
